import { useRef, useEffect, useCallback } from "react";
import { isFromWidget } from "../utils/widgetEvents";

interface UserAction {
    type: 'click' | 'navigation' | 'scroll' | 'input';
    timestamp: number;
    data: {
        elementTag?: string;
        elementText?: string;
        elementId?: string;
        elementClass?: string;
        url?: string;
        scrollY?: number;
        inputType?: string;
    };
}

/**
 * A short label for what was clicked — but only for controls, whose text is
 * UI copy ("Add to basket", "Next"). Arbitrary elements' text can be a
 * customer's own data (an email address, an order), which has no business in
 * a feedback ticket.
 */
const labelOf = (target: Element): string | undefined => {
    const control = target.closest('button, a, [role="button"], label, summary');
    if (!control) return undefined;
    const text =
        control.getAttribute('aria-label') || control.textContent || '';
    return text.replace(/\s+/g, ' ').trim().slice(0, 50) || undefined;
};

export const useSessionRecording = () => {
    const actionsRef = useRef<UserAction[]>([]);
    const maxActions = 50; // Keep last 50 actions

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const recordAction = (action: UserAction) => {
            if (actionsRef.current.length >= maxActions) {
                actionsRef.current.shift();
            }
            actionsRef.current.push(action);
        };

        // Record clicks
        const handleClick = (e: MouseEvent) => {
            if (isFromWidget(e)) return;
            const target = e.target as Element;

            recordAction({
                type: 'click',
                timestamp: Date.now(),
                data: {
                    elementTag: target.tagName.toLowerCase(),
                    elementText: labelOf(target),
                    elementId: target.id || undefined,
                    elementClass: target.className && typeof target.className === 'string' 
                        ? target.className.split(' ').slice(0, 3).join(' ') 
                        : undefined,
                }
            });
        };

        // Record navigation
        const handleNavigation = () => {
            recordAction({
                type: 'navigation',
                timestamp: Date.now(),
                data: {
                    url: window.location.href
                }
            });
        };

        // Record scroll (throttled)
        let scrollTimeout: NodeJS.Timeout;
        const handleScroll = () => {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
                recordAction({
                    type: 'scroll',
                    timestamp: Date.now(),
                    data: {
                        scrollY: window.scrollY
                    }
                });
            }, 500);
        };

        // Record input events
        const handleInput = (e: Event) => {
            if (isFromWidget(e)) return;
            const target = e.target as HTMLInputElement;

            recordAction({
                type: 'input',
                timestamp: Date.now(),
                data: {
                    elementTag: target.tagName.toLowerCase(),
                    elementId: target.id || undefined,
                    inputType: target.type || undefined,
                }
            });
        };

        // Record initial page load
        recordAction({
            type: 'navigation',
            timestamp: Date.now(),
            data: { url: window.location.href }
        });

        document.addEventListener('click', handleClick, true);
        window.addEventListener('popstate', handleNavigation);
        // Client-side route changes (Next.js, React Router): useCurrentUrl
        // patches history and broadcasts them as this event.
        window.addEventListener('markup:locationchange', handleNavigation);
        window.addEventListener('scroll', handleScroll, { passive: true });
        document.addEventListener('input', handleInput, true);

        return () => {
            document.removeEventListener('click', handleClick, true);
            window.removeEventListener('popstate', handleNavigation);
            window.removeEventListener('markup:locationchange', handleNavigation);
            window.removeEventListener('scroll', handleScroll);
            document.removeEventListener('input', handleInput, true);
            clearTimeout(scrollTimeout);
        };
    }, []);

    const getSessionEvents = useCallback(() => {
        if (actionsRef.current.length === 0) {
            return undefined;
        }
        return JSON.stringify(actionsRef.current);
    }, []);

    const clearSession = useCallback(() => {
        actionsRef.current = [];
    }, []);

    return {
        getSessionEvents,
        clearSession
    };
};
